import { AfterViewInit, Directive, InputSignal, QueryList, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { MatOption } from '@angular/material/core';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription, merge } from 'rxjs';
import { delay, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { SelectComponent } from './select.component';
import { TiimeOptionSelectionChange } from '../option';

export const TIIME_SELECT_MULTIPLE_OPTION_ID = 'tiime-select-multiple-option';

@UntilDestroy({ checkProperties: true })
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'mat-option[tiimeSelectMultipleOption]',
  host: {
    class: 'tiime-select-multiple-option'
  }
})
export class SelectMultipleOptionDirective<T> implements AfterViewInit {
  readonly value: InputSignal<T[]> = input.required<T[]>();

  private readonly subscription: Subscription = new Subscription();

  private get selectControl(): AbstractControl {
    return this.select?.ngControl?.control;
  }

  private get selectControlValue(): T[] {
    return this.selectControl?.value;
  }

  private get allValuesIncluded(): boolean {
    return (
      this.value()?.length === this.selectControlValue?.length &&
      this.value()?.every((value: T) => this.selectControlValue?.includes(value))
    );
  }

  private get selectControlValueWithoutAllValues(): T[] {
    return this.selectControlValue?.filter((value: T) => !this.value()?.includes(value));
  }

  constructor(
    private readonly option: MatOption,
    private readonly select: SelectComponent
  ) {
    this.option.id = TIIME_SELECT_MULTIPLE_OPTION_ID;
    this.observeValueChanges();
  }

  ngAfterViewInit(): void {
    this.observeAllOptionChange();
    this.observeOtherOptionsChange();
  }

  // On coche ou on décoche l'option lorsque la valuur change
  private observeValueChanges(): void {
    this.subscription.add(
      toObservable(this.value)
        .pipe(
          distinctUntilChanged(),
          delay(50),
          tap(() => {
            if (this.allValuesIncluded) {
              this.selectOption();
            } else {
              this.deselectOption();
            }
          })
        )
        .subscribe()
    );
  }

  // On coche ou on décoche les autres options lorsqu'on clique sur l'option
  private observeAllOptionChange(): void {
    this.subscription.add(
      this.option.onSelectionChange
        .pipe(
          filter((ev: TiimeOptionSelectionChange) => ev.isUserInput),
          tap((ev: TiimeOptionSelectionChange) => {
            if (ev.source.selected) {
              this.setSelectControlValue(this.value());
              this.selectOption();
            } else {
              this.setSelectControlValue(this.selectControlValueWithoutAllValues);
              this.deselectOption();
            }
          })
        )
        .subscribe()
    );
  }

  // On coche ou on décoche l'option lorsqu'on clique sur une autre option
  private observeOtherOptionsChange(): void {
    this.subscription.add(
      this.select.optionsChanges
        .pipe(
          switchMap((options: QueryList<MatOption>) => merge(...options.map(option => option.onSelectionChange))),
          filter((ev: TiimeOptionSelectionChange) => ev.isUserInput && ev.source.value !== this.option.value),
          tap(() => {
            if (this.allValuesIncluded) {
              this.selectOption();
            } else {
              this.deselectOption();
            }
          })
        )
        .subscribe()
    );
  }

  private selectOption(): void {
    this.option?.select(false);
  }

  private deselectOption(): void {
    this.option?.deselect(false);
  }

  private setSelectControlValue(value: T[]): void {
    this.selectControl?.setValue(value);
    this.propagateChanges(value);
  }

  private propagateChanges(value: T[]): void {
    this.select?.propagateChanges(value);
  }
}
